/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import clsx from "clsx"
import { observer } from "mobx-react-lite"
import type { KeyboardEvent } from "react"

import type { ApplicationType } from "../../features/application-type/application-type-types"
import { valueOrDash } from "../../utils/value-or-dash"
import { AppIcon } from "../components/app-icon"
import { AppTitleDescription } from "../components/app-title-description"
import { CategoryPlatform } from "../components/category-platform"

import "./application-type-view.css"

interface Props {
    appType: ApplicationType
    selected: boolean
    selectable: boolean
    onClick?: (appType: ApplicationType) => void
}

// Keyboard keys used to activate the row, matching the activation keys of a
// native button.
const activationKeys = ["Enter", " "]

export const ApplicationTypeRowView = observer(({ appType, selected, selectable, onClick }: Props) => {
    const isClickable = onClick !== undefined

    /*
     * Handle keyboard activation for the focusable row without changing its
     * implicit `row` role. Using `role="button"` would replace the row semantics
     * while its cells still belong to it. In a selectable table, the checkbox
     * remains responsible for exposing the selection state.
     */
    const handleKeyDown = (e: KeyboardEvent<HTMLTableRowElement>) => {
        if (!activationKeys.includes(e.key)) {
            return
        }

        e.preventDefault()
        handleClick()
    }

    const handleClick = () => {
        onClick?.(appType)
    }

    return (
        <tr
            className={clsx("application-type-row-view", { "cursor-pointer": isClickable })}
            tabIndex={isClickable ? 0 : undefined}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
        >
            {selectable && (
                <td className="col-check">
                    <input
                        type="checkbox"
                        className="form-check-input"
                        checked={selected}
                        readOnly={true}
                        aria-label={`Select ${appType.sourcePackageName}`}
                    />
                </td>
            )}
            <td className="col-icon">
                <AppIcon appTypeId={appType.id} />
            </td>
            <td className="col-name">
                <div className="text-section">
                    <AppTitleDescription
                        description={valueOrDash(appType.description)}
                        sourcePackageName={valueOrDash(appType.sourcePackageName)}
                    />
                </div>
            </td>
            <td className="col-badge">
                <CategoryPlatform category={appType.category} platform={appType.platform} />
            </td>
        </tr>
    )
})
